import { ObjectValues } from 'types';
import { DataRoomNode } from './DataRoomNode';
import { DataRoomMember } from './DataRoomMember';
import { DataRoomSecuritySettings } from './DataRoom';

export const AVAILABILITY_STATE = {
  ON: 'on',
  OFF: 'off',
  MIXED: 'mixed',
} as const;

export type BulkAvailabilityState = ObjectValues<typeof AVAILABILITY_STATE>;

type AvailabilityAction = {
  disabled?: boolean;
  canDowngrade?: boolean;
  state: BulkAvailabilityState;
};

export type AvailabilityActionFormUtility = {
  checked?: boolean;
  isMixed?: boolean;
};

export type BulkAvailabilityMember = {
  _id: string; // Data Room member id
  name: string; // Data Room member name
  isLead?: boolean;

  view: AvailabilityAction;
  download: AvailabilityAction;

  fileExpirationDateInDays?: number;
};

export type BulkFormAvailabilityMember = {
  _id: string; // Data Room member id
  name: string; // Data Room member name
  isLead?: boolean;

  view: AvailabilityAction & AvailabilityActionFormUtility;
  download: AvailabilityAction & AvailabilityActionFormUtility;

  fileExpirationDateInDays?: number;
};

export type BulkAvailabilityBuyGroup = {
  name: string;
  isSingleMemberGroup?: boolean;
  dataRoomMembers: BulkAvailabilityMember[];
};

export type BulkAvailabilityBuyGroupResponse = {
  name: string;
  dataRoomMembers: DataRoomMember[];
};

export type BulkAvailabilityMembersResponse = {
  dataRoomNodes: Partial<DataRoomNode>[];
};

export type BulkAvailabilityMembers = {
  buySideGroups: BulkAvailabilityBuyGroup[];
  sellSideMembers: BulkAvailabilityMember[];
  fileSettings: {
    expirationDays: 'mixed' | number;
    allowSoftSignals?: BulkAvailabilityState;
    downloadSecurePdf: BulkAvailabilityState;
    hasExpiration: BulkAvailabilityState;
    // watermark
    includeWatermark: BulkAvailabilityState;
    // watermark appearance
    watermarkDiagonal: BulkAvailabilityState;
    watermarkHeader: BulkAvailabilityState;
    watermarkFooter: BulkAvailabilityState;
    // watermark buyers info
    watermarkFullName: BulkAvailabilityState;
    watermarkEmail: BulkAvailabilityState;
    watermarkIpAddress: BulkAvailabilityState;
  };
};

export type BulkAvailabilityBuySideMembersForm = {
  name: string;
  isSingleMemberGroup?: boolean;
  dataRoomMembers: BulkFormAvailabilityMember[];
};

export type BulkAvailabilityMembersForm = {
  buySideGroups: BulkAvailabilityBuySideMembersForm[];
  sellSideMembers: BulkFormAvailabilityMember[];
  allowSoftSignals?: AvailabilityActionFormUtility;
} & BulkFileSettings;

export type BulkFileSettings = {
  expirationDays: number;
  downloadSecurePdf: AvailabilityActionFormUtility;
  hasExpiration: AvailabilityActionFormUtility;
  includeWatermark: AvailabilityActionFormUtility;
  watermarkDiagonal: AvailabilityActionFormUtility;
  watermarkHeader: AvailabilityActionFormUtility;
  watermarkFooter: AvailabilityActionFormUtility;
  watermarkFullName: AvailabilityActionFormUtility;
  watermarkEmail: AvailabilityActionFormUtility;
  watermarkIpAddress: AvailabilityActionFormUtility;
};

export type BulkAvailabilityUpdateMembers = {
  allowSoftSignals?: boolean;
  changedPermissions: {
    dataRoomMemberId: string;
    enable: boolean;
  }[];
} & DataRoomSecuritySettings;

export type BulkAvailabilityFileSettings = {
  expirationDays: number;
  downloadSecurePdf: AvailabilityAction & AvailabilityActionFormUtility;
  hasExpiration: AvailabilityAction & AvailabilityActionFormUtility;
  includeWatermark: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkDiagonal: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkHeader: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkFooter: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkFullName: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkEmail: AvailabilityAction & AvailabilityActionFormUtility;
  watermarkIpAddress: AvailabilityAction & AvailabilityActionFormUtility;
};
